if (!customElements.get("product-quickview")) {
  class ProductQuickview extends HTMLElement {
    constructor() {
      super();

      // Define empty references for elements to be loaded dynamically
      this.priceEl = null;
      this.optionsEl = null;
      this.addonsEl = null;
      this.stockIndicatorEl = null;
      this.productFormEl = null;
      this.productFormIdInputEl = null;
      this.closeButtonEl = null;
      this.background = null;

      // Bind methods for listeners
      this.updateProductOptions = this.updateProductOptions.bind(this);
      this.updateAddonOptions = this.updateAddonOptions.bind(this);
      this.fetchAndRenderContent = this.fetchAndRenderContent.bind(this);

      // Listen for the custom event from ProductQuickviewButton instances
      this.addEventListener("quickview-open", (event) => {
        this.fetchAndRenderContent(event.detail.url);
      });
    }
    connectedCallback() {
      // Event listener to open quick view from custom trigger button event
      this.initListeners();
    }

    initListeners() {
      // Only needed once on setup
      document.addEventListener("click", this.handleOutsideClick.bind(this));
    }

    // Fetch HTML content from the URL and populate the modal
    async fetchAndRenderContent(url) {
      try {
        const response = await fetch(url);
        const html = await response.text();

        // Parse the returned HTML into a DOM structure
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // Select the .js-product-quickview-inner element from the parsed HTML
        const innerContent = doc.querySelector(
          ".js-product-quickview-inner-wrapper",
        );

        // Ensure content exists before rendering
        if (innerContent) {
          this.innerHTML = ""; // Clear existing content
          this.appendChild(innerContent); // Append only the inner content to the custom element
          this.reinitialiseContent(); // Reinitialise listeners and elements
          this.openQuickView();
        } else {
          console.warn(
            "No .js-product-quickview-inner element found in the response.",
          );
        }
      } catch (error) {
        console.error("Error fetching Quick View content:", error);
      }
    }

    openQuickView() {
      this.classList.remove("hidden");

      // Bind the handleOutsideClick method to retain the correct `this` context
      this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);

      // Add an event listener for detecting outside clicks
      document.addEventListener("click", this.boundHandleOutsideClick);
    }

    closeQuickView() {
      this.classList.add("hidden");

      // Remove the exact event listener for outside clicks
      document.removeEventListener("click", this.boundHandleOutsideClick);
    }

    // Handle outside click to close modal
    handleOutsideClick(event) {
      if (event.target === this.background || !this.contains(event.target)) {
        this.closeQuickView();
      }
    }

    // Reinitialise all elements and listeners for newly loaded content
    reinitialiseContent() {
      // Re-assign elements within the dynamically fetched HTML
      this.priceEl = this.querySelector(".product-price");
      this.optionsEl = this.querySelector(".product-form__options");
      this.addonsEl = this.querySelector(".product-form__add-ons");
      this.stockIndicatorEl = this.querySelector(
        ".product-form__stock-indicator",
      );
      this.productFormEl = this.querySelector('[data-type="add-to-cart-form"]');
      this.productFormIdInputEl =
        this.productFormEl?.querySelector('[name="id"]');
      this.closeButtonEl = this.querySelector(".js-product-quickview-close");
      // Close button listener
      if (this.closeButtonEl) {
        this.closeButtonEl.addEventListener("click", () => {
          this.closeQuickView();
        });
      }

      this.background = this.querySelector(".product-lightbox__background");

      // Initialise options, add-ons, and form listeners
      this.initProductOptions();
      this.initAddonOptions();
    }

    initProductOptions() {
      if (!this.optionsEl) return;
      this.optionsEl
        .querySelectorAll(".product-form__option")
        .forEach((fieldset) => {
          const variantButtons = fieldset.querySelectorAll(
            ".variant-button > input",
          );
          variantButtons.forEach((variantInput) => {
            variantInput.addEventListener(
              "change",
              this.updateProductOptions,
              false,
            );
          });
        });
    }

    updateProductOptions(e) {
      const productVariantOptions = JSON.parse(
        this.optionsEl.dataset.productOptions,
      );

      // Update variant label
      const labelEl = e.target
        .closest("fieldset")
        .querySelector("[data-legend]");
      const variantLabel = e.target.value;
      labelEl.textContent = variantLabel;

      // Get selected options
      const selectedOptions = ["", "", ""];
      let productVariantOptionIndex = 0;
      this.optionsEl
        .querySelectorAll(".product-form__option")
        .forEach((options) => {
          options.querySelectorAll("input").forEach((input) => {
            if (input.checked) {
              selectedOptions[productVariantOptionIndex] = input.value;
            }
          });
          productVariantOptionIndex++;
        });

      // Get selected variant from selected options
      const selectedOption = productVariantOptions.filter(
        (item) =>
          item.option1 === selectedOptions[0] &&
          item.option2 === selectedOptions[1] &&
          item.option3 === selectedOptions[2],
      );

      // If the selected options provide a match, update the product info and form
      if (selectedOption.length) {
        //Update product prices
        this.updatePrice(
          selectedOption[0]["price"],
          selectedOption[0]["compare_at_price"],
        );

        // Update stock count
        this.updateStockIndicator(
          selectedOption[0]["inventory_policy"],
          selectedOption[0]["inventory"],
        );

        // Update product form ID input
        this.productFormIdInputEl.value = selectedOption[0]["id"];

        // Enable/Disable Buy Button
        const buyButton = this.productFormEl.querySelector('[type="submit"]');
        if (selectedOption[0]["available"]) {
          if (buyButton.disabled) {
            const availableText = buyButton.dataset.availableText;
            buyButton.querySelector(".btn__content > span").textContent =
              availableText;
            buyButton.classList.remove("product-form__submit--sold-out");
            buyButton.disabled = false;
          }
        } else {
          // Sold out
          const unavailableText = buyButton.dataset.unavailableText;
          buyButton.querySelector(".btn__content > span").textContent =
            unavailableText;
          buyButton.classList.add("product-form__submit--sold-out");
          buyButton.disabled = true;
        }
      }

      // Update colour product URLs
      this.updateOptionUrls(e.target);
      this.disableOutOfStockOptions(e, selectedOptions, productVariantOptions);
    }

    disableOutOfStockOptions(e, selectedOptions, productVariantOptions) {
      const selectedOptionSize = selectedOptions.filter(option => option.length).length;
      this.availableOptions = this.optionsEl.querySelectorAll('.product-form__option');
      const availableOptionSize = this.availableOptions.length;
      this.inputs = this.optionsEl.querySelectorAll('input');
      this.fieldsets = this.optionsEl.querySelectorAll('.product-form__option');

      // Disable out-of-stock options
      // Liquid takes care of out-of-stock options when availableOptionSize == 1
      if ((availableOptionSize === 3 && selectedOptionSize >= 2) || availableOptionSize === 2) {
        // Enable all options
        this.inputs.forEach(input => {
          input.removeAttribute('data-disabled');
        });

        this.fieldsets.forEach((fieldset, index) => {
          fieldset.querySelectorAll('input').forEach((input) => {
            // Create a copy of selectedOptions and update the current index with the input value
            const updatedSelectedOptions = [...selectedOptions];
            updatedSelectedOptions[index] = input.value;

            // Find the variant that matches the updated options
            const variant = productVariantOptions.find(variant => {
              // Ensure all updated options match the variant options
              return variant.option1 === updatedSelectedOptions[0] &&
                     variant.option2 === updatedSelectedOptions[1] &&
                     variant.option3 === updatedSelectedOptions[2];
            });

            // Disable the input if the variant is out of stock (inventory 0)
            if (variant && !variant.available) {
              input.setAttribute('data-disabled', '');
            }
          });
        });
      }
    }

    updatePrice(price, compare) {
      if (!this.priceEl) return;
      const singlePriceEl = this.priceEl.querySelector("[data-single-price]");
      const comparePriceEl = this.priceEl.querySelector("[data-compare-price]");

      if (!compare) {
        singlePriceEl.textContent = price.replace(/<[^>]*>?/gm, "");
        singlePriceEl.classList.remove("hidden");
        comparePriceEl.classList.add("hidden");
      } else {
        comparePriceEl.querySelector("[data-price]").textContent =
          price.replace(/<[^>]*>?/gm, "");
        comparePriceEl.querySelector("[data-compare]").textContent =
          compare.replace(/<[^>]*>?/gm, "");

        const newPrice = parseInt(price.replace(/[^0-9\.-]+/g, ""));
        const oldPrice = parseInt(compare.replace(/[^0-9\.-]+/g, ""));
        const discount = ((oldPrice - newPrice) * 100) / oldPrice;
        comparePriceEl.querySelector(".badge-xs").textContent =
          Math.round(discount) + "% off";

        singlePriceEl.classList.add("hidden");
        comparePriceEl.classList.remove("hidden");
      }
    }

    updateStockIndicator(policy, quanity) {
      if (!this.stockIndicatorEl) return;
      // Get text and colour from data attributes set in theme settings
      const lowStockFreshold = parseInt(
        this.stockIndicatorEl.dataset.lowStockThreshold,
      );
      let text = "";
      let color = "";
      if (policy == "continue") {
        text = this.stockIndicatorEl.dataset.inStockText;
        color = this.stockIndicatorEl.dataset.inStockColour;
      } else if (quanity <= 0) {
        text = this.stockIndicatorEl.dataset.soldOutText;
        color = this.stockIndicatorEl.dataset.soldOutColour;
      } else if (quanity <= lowStockFreshold) {
        text = this.stockIndicatorEl.dataset.lowStockText.replace("#", quanity);
        color = this.stockIndicatorEl.dataset.lowStockColour;
      } else {
        text = this.stockIndicatorEl.dataset.inStockText;
        color = this.stockIndicatorEl.dataset.inStockColour;
      }

      // Update stock indicator content and show
      const stockIndicatorTextEl = this.stockIndicatorEl.querySelector(
        "[data-inventory-indicator-text]",
      );
      stockIndicatorTextEl.textContent = text;
      this.stockIndicatorEl.style.setProperty("--stock-color", color);
      if (policy == "deny" && quanity <= 0) {
        this.stockIndicatorEl.classList.add("hidden");
      } else {
        this.stockIndicatorEl.classList.remove("hidden");
      }
    }

    initAddonOptions() {
      if (!this.addonsEl) return;
      this.addonsEl
        .querySelectorAll(".product-form__add-on")
        .forEach((fieldset) => {
          const variantButtons = fieldset.querySelectorAll(
            ".variant-button > input",
          );
          variantButtons.forEach((variantInput) => {
            variantInput.addEventListener(
              "change",
              this.updateAddonOptions,
              false,
            );
          });
        });
    }

    updateAddonOptions(e) {
      // Updates selected labels
      const labelEl = e.target
        .closest("fieldset")
        .querySelector("[data-add-on-variant]");
      const variantLabel = e.target.value;
      labelEl.textContent = variantLabel;

      // Updates add on properties for line items
      const propertyValue = e.target.value;
      const propertyName = e.target.dataset.propertyName;
      document.querySelector(
        '[name="properties[' + propertyName + ']"]',
      ).value = propertyValue;
    }

    updateOptionUrls(option) {
      const productColourOptions = document.querySelectorAll(
        ".product-options__colour-url",
      );
      if (productColourOptions.length == 0) return;

      const relatedProductOptions = JSON.parse(
        document.querySelector(".product-options").dataset.relatedProducts,
      );
      const optionName = option.dataset.option;
      const optionValue = option.value;

      productColourOptions.forEach((colourUrl) => {
        const productId = colourUrl.dataset.productId;
        const baseUrl = colourUrl.dataset.baseUrl;
        const relatedProduct = relatedProductOptions.find(
          (item) => item.product === productId,
        );
        if (!relatedProduct) return;
        const colourOption = relatedProduct["options"].find(
          (item) => item.option === optionName,
        );
        if (!colourOption) return;
        const selectedColourOption = colourOption["values"].find(
          (item) => item.name === optionValue,
        );
        if (!selectedColourOption) return;
        colourUrl.href = baseUrl + "?variant=" + selectedColourOption["id"];
      });
    }
  }

  customElements.define("product-quickview", ProductQuickview);
}

if (!customElements.get("product-quickview-button")) {
  class ProductQuickviewButton extends HTMLElement {
    constructor() {
      super();
      this.productQuickview = document.querySelector("product-quickview");

      // Bind the click event to open the Quick View
      this.addEventListener("click", this.handleClick.bind(this));
    }

    handleClick() {
      if (!this.productQuickview) return;
      const targetUrl = this.getAttribute("data-url");
      if (targetUrl) {
        // Dispatch a custom event with the URL for the ProductQuickview component to listen to
        this.productQuickview.dispatchEvent(
          new CustomEvent("quickview-open", {
            bubbles: true,
            detail: { url: targetUrl },
          }),
        );
      }
    }
  }

  customElements.define("product-quickview-button", ProductQuickviewButton);
}
